import PersonImage from '@/components/PersonImage';
import Constants from '@/Constants';
import { Person } from '@/models/Person';
import FollowModal from '@/pages/Profile/FollowModal';
import { getDisplayName } from '@/services/UtilService';
import { ApplicationState } from '@/store';
import { getPublicPersonRequest, updateMyPersonRequest } from '@/store/person/action';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Route, Switch, useHistory } from 'react-router-dom';
import ConfigurationsPage from './Configurations';
import ProfileEditModal from './EditModal';
import LearningMapPage from './LearningMap';
import './style.scss';
import TopicsOfInterestPage from './TopicsOfInterest';

interface MenuItemType {
    route: string;
    label: string;
}

const ProfilePage: React.FC = () => {
    const { strings, person, publicPerson, listOfFollowing, listOfFollowers } = useSelector((state: ApplicationState) => ({
        strings: state.locale.data,
        person: state.identity.data.person,
        publicPerson: state.people.publicPerson || ({} as Person),
        listOfFollowing: state.followers.data.following,
        listOfFollowers: state.followers.data.followers,
    }));

    const [showProfileEditModal, setShowProfileEditModal] = useState<boolean>(false);
    const [showFollowersModal, setShowFollowersModal] = useState<boolean>(false);
    const [disabledFollowersModal, setDisabledFollowersModal] = useState<boolean>(false);
    const [disabledFollowingModal, setDisabledFollowingModal] = useState<boolean>(false);

    const history = useHistory();
    const dispatch = useDispatch();

    const profilePath = '/profile';

    const isSelected = (route: string) => (history.location.pathname.indexOf(route) > -1 ? 'selected' : '');

    const menuItems: MenuItemType[] = [
        { route: `${profilePath}/learningmap`, label: strings.PROFILE_MENU.LEARNING_MAP },
        { route: `${profilePath}/topicsofinterest`, label: strings.PROFILE_MENU.TOPICS_OF_INTEREST },
        //{ route: `${profilePath}/configurations`, label: strings.PROFILE_MENU.CONFIGURATIONS },
    ];

    useEffect(() => {
        const personId = person.id as number;
        dispatch(getPublicPersonRequest(personId));
    }, [listOfFollowing.length, listOfFollowers.length]);

    useEffect(() => {
        if (
            strings._LANGUAGE !== Constants.language.PT ||
            (person.dynamicAttributes &&
                person.dynamicAttributes.selectedProfile &&
                person.dynamicAttributes.selectedProfile !== Constants.STUDENT_PROFILE)
        ) {
            history.replace('/');
        }
    }, [strings._LANGUAGE, person.dynamicAttributes]);

    const onProfileEditModalClose = () => {
        dispatch(updateMyPersonRequest());
        dispatch(getPublicPersonRequest(person.id as number));
        setShowProfileEditModal(false);
    };

    const openFollowModal = (tabToOpen: string) => {
        localStorage.setItem(Constants.SELECTED_TAB_FOLLOW_MODAL, tabToOpen);
        setShowFollowersModal(true);
    };

    useEffect(() => {
        setDisabledFollowingModal(publicPerson.following === 0);
        setDisabledFollowersModal(publicPerson.followers === 0);
    }, [publicPerson.following, publicPerson.followers]);

    return (
        <div className="profile-page">
            <div className="container">
                <div className="menu">
                    {publicPerson.id && (
                        <>
                            <FollowModal ownerId={publicPerson.id} setShow={setShowFollowersModal} show={showFollowersModal} />
                            <div className="user-info">
                                <PersonImage size={84} src={publicPerson.thumbnail} />
                                <button className="edit-link" onClick={() => setShowProfileEditModal(true)}>
                                    <i className="material-icons">{Constants.icons.EDIT}</i>
                                </button>
                                <span className="user-name">{getDisplayName(publicPerson)}</span>
                                {publicPerson.position && <span className="user-position">{publicPerson.position}</span>}
                                {publicPerson.email && <span className="user-email">{publicPerson.email}</span>}
                                {publicPerson.departmentName && <span className="user-department">{publicPerson.departmentName}</span>}
                                {publicPerson.aboutMe && <span className="user-about">{publicPerson.aboutMe}</span>}

                                <div className="followers-container">
                                    <div
                                        className={`followers-info ${disabledFollowersModal ? 'disabled' : ''}`}
                                        onClick={() => !disabledFollowersModal && openFollowModal('followers')}
                                    >
                                        <strong>{publicPerson.followers}</strong>
                                        <span>{publicPerson.followers === 1 ? strings.FOLLOWER : strings.FOLLOWERS}</span>
                                    </div>
                                    <div
                                        className={`followers-info ${disabledFollowingModal ? 'disabled' : ''}`}
                                        onClick={() => !disabledFollowingModal && openFollowModal('following')}
                                    >
                                        <strong>{publicPerson.following}</strong>
                                        <span>{strings.FOLLOWING}</span>
                                    </div>
                                </div>
                            </div>
                        </>
                    )}
                    {menuItems.map((menuItem: MenuItemType, index: number) => (
                        <div
                            className={`menu-item ${isSelected(menuItem.route)}`}
                            key={index}
                            onClick={() => {
                                history.push(menuItem.route);
                            }}
                            role="button"
                        >
                            <span>{menuItem.label}</span>
                            <i className="material-icons-outlined">{Constants.icons.ARROW_RIGHT}</i>
                        </div>
                    ))}
                </div>
                <div className="content">
                    <Switch>
                        <Route exact path={`${profilePath}/learningmap`}>
                            <LearningMapPage />
                        </Route>
                        <Route exact path={`${profilePath}/topicsofinterest`}>
                            <TopicsOfInterestPage />
                        </Route>
                        <Route exact path={`${profilePath}/configurations`}>
                            <ConfigurationsPage />
                        </Route>
                    </Switch>
                    <ProfileEditModal handleModalClose={onProfileEditModalClose} showProfileEditModal={showProfileEditModal} />
                </div>
            </div>
        </div>
    );
};
export default ProfilePage;
